import { useCurrentUser } from './useCurrentUser';

export type TenantTaxRegime =
  | 'INDIA_GST'
  | 'US_SALES_TAX'
  | 'EU_VAT'
  | 'UK_VAT'
  | 'GCC_VAT'
  | 'NONE';

export interface TenantLocale {
  /** ISO 3166-1 alpha-2 (e.g. 'IN', 'US', 'GB'). Defaults to 'IN' for legacy/unknown. */
  country: string;
  /** ISO 4217 (e.g. 'INR', 'USD'). Defaults to 'INR' for legacy/unknown. */
  currency: string;
  /** Calendar month (1-12) on which the FY starts. */
  fiscalYearStartMonth: number;
  taxRegime: TenantTaxRegime;
  /**
   * `true` when the tenant is in India under the GST regime — gates
   * GSTIN/PAN form fields, IGST/CGST/SGST UI copy, and India-specific
   * regulatory features (e-invoice, e-way bill).
   */
  isIndia: boolean;
  /**
   * Country-aware copy. Defaults are India-friendly so legacy callers
   * don't break. Non-India regimes use generic terminology ("Tax ID",
   * "Postal code") until per-country profiles ship.
   */
  labels: TenantLabelSet;
}

export interface TenantLabelSet {
  /** Tax invoice header — varies by jurisdiction. */
  invoiceTitle: string;
  /** Top-level tax ID label on customer/vendor forms. India = "GSTIN", US = "EIN", UK = "VAT no.", generic = "Tax ID". */
  taxIdLabel: string;
  /** Address-level tax ID label (when applicable). */
  addressTaxIdLabel: string;
  /** Postal code label — "PIN" in India, "ZIP" in US, "Postcode" in UK. */
  postalCodeLabel: string;
  /** Whether to show the secondary tax ID field (India PAN). */
  showSecondaryTaxId: boolean;
  /** Label for that secondary field (India = "PAN"). */
  secondaryTaxIdLabel: string;
  /** State-code placeholder hint shown in address forms. */
  stateHint: string;
}

const LABELS: Record<TenantTaxRegime, TenantLabelSet> = {
  INDIA_GST: {
    invoiceTitle: 'TAX INVOICE',
    taxIdLabel: 'GSTIN',
    addressTaxIdLabel: 'GSTIN',
    postalCodeLabel: 'PIN',
    showSecondaryTaxId: true,
    secondaryTaxIdLabel: 'PAN',
    stateHint: '2-letter code (MH/KA/...)',
  },
  US_SALES_TAX: {
    invoiceTitle: 'INVOICE',
    taxIdLabel: 'EIN',
    addressTaxIdLabel: 'Tax ID',
    postalCodeLabel: 'ZIP',
    showSecondaryTaxId: false,
    secondaryTaxIdLabel: '',
    stateHint: '2-letter state (CA/NY/...)',
  },
  EU_VAT: {
    invoiceTitle: 'INVOICE',
    taxIdLabel: 'VAT ID',
    addressTaxIdLabel: 'VAT ID',
    postalCodeLabel: 'Postal code',
    showSecondaryTaxId: false,
    secondaryTaxIdLabel: '',
    stateHint: 'Region (optional)',
  },
  UK_VAT: {
    invoiceTitle: 'TAX INVOICE',
    taxIdLabel: 'VAT no.',
    addressTaxIdLabel: 'VAT no.',
    postalCodeLabel: 'Postcode',
    showSecondaryTaxId: false,
    secondaryTaxIdLabel: '',
    stateHint: 'County (optional)',
  },
  GCC_VAT: {
    invoiceTitle: 'TAX INVOICE',
    taxIdLabel: 'TRN',
    addressTaxIdLabel: 'TRN',
    postalCodeLabel: 'Postal code',
    showSecondaryTaxId: false,
    secondaryTaxIdLabel: '',
    stateHint: 'Emirate / region',
  },
  NONE: {
    invoiceTitle: 'INVOICE',
    taxIdLabel: 'Tax ID',
    addressTaxIdLabel: 'Tax ID',
    postalCodeLabel: 'Postal code',
    showSecondaryTaxId: false,
    secondaryTaxIdLabel: '',
    stateHint: 'State / region',
  },
};

/**
 * Per-tenant locale (Slice 18 multi-country). Returns the four core
 * fields hydrated from `/auth/me` plus a `labels` set that drives
 * country-aware form copy.
 *
 * Defaults to India when the user payload doesn't carry locale fields
 * (legacy sessions, PLATFORM_ADMIN scope) so existing screens keep
 * working without breakage.
 */
export function useTenantLocale(): TenantLocale {
  const me = useCurrentUser();
  const country = me?.tenantCountry ?? 'IN';
  const currency = me?.tenantCurrency ?? 'INR';
  const fiscalYearStartMonth = me?.tenantFiscalYearStartMonth ?? 4;
  const taxRegime = (me?.tenantTaxRegime ?? 'INDIA_GST') as TenantTaxRegime;

  return {
    country,
    currency,
    fiscalYearStartMonth,
    taxRegime,
    isIndia: country === 'IN' && taxRegime === 'INDIA_GST',
    labels: LABELS[taxRegime],
  };
}
